import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GAME_DATA } from './QuizGame';
import { Trophy, RotateCcw, Target, CheckCircle2, Users, TrendingUp, Medal, Crown, Award } from 'lucide-react';
import { SPECIALTY_COLORS, Specialty } from '../types';
interface QuizResultsScreenProps {
  score: number;
  totalQuestions: number;
  roundScores: number[];
  onPlayAgain: () => void;
}
interface LeaderboardEntry {
  name: string;
  score: number;
  accuracy: number;
  isYou?: boolean;
}
// Simulated team leaderboard
const SIMULATED_PLAYERS: Omit<LeaderboardEntry, 'isYou'>[] = [{
  name: 'Sarah M.',
  score: 12,
  accuracy: 100
}, {
  name: 'James K.',
  score: 11,
  accuracy: 92
}, {
  name: 'Dr. Patel',
  score: 10,
  accuracy: 83
}, {
  name: 'Lisa W.',
  score: 9,
  accuracy: 75
}, {
  name: 'Tom R.',
  score: 8,
  accuracy: 67
}, {
  name: 'Anna B.',
  score: 7,
  accuracy: 58
}, {
  name: 'Mark D.',
  score: 6,
  accuracy: 50
}, {
  name: 'Emily S.',
  score: 5,
  accuracy: 42
}, {
  name: 'Chris L.',
  score: 4,
  accuracy: 33
}];
function getPerformanceTier(accuracy: number): {
  label: string;
  color: string;
  bg: string;
  icon: React.ElementType;
  message: string;
} {
  if (accuracy === 100) return {
    label: 'Perfect',
    color: '#f59e0b',
    bg: 'bg-amber-50',
    icon: Crown,
    message: 'Flawless! You know the specialty landscape inside and out.'
  };
  if (accuracy >= 83) return {
    label: 'Expert',
    color: '#22c55e',
    bg: 'bg-green-50',
    icon: Trophy,
    message: 'Excellent understanding of specialty behaviors.'
  };
  if (accuracy >= 58) return {
    label: 'Proficient',
    color: '#3b82f6',
    bg: 'bg-blue-50',
    icon: Award,
    message: 'Good foundation — a few gaps to close.'
  };
  if (accuracy >= 33) return {
    label: 'Developing',
    color: '#f97316',
    bg: 'bg-orange-50',
    icon: TrendingUp,
    message: 'Room to grow. Review the insights below.'
  };
  return {
    label: 'Beginner',
    color: '#ef4444',
    bg: 'bg-red-50',
    icon: Target,
    message: 'Time to study up! The insights below will help.'
  };
}
function getPastScores(): number[] {
  try {
    const stored = localStorage.getItem('quiz_past_scores');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}
function savePastScore(score: number) {
  try {
    const past = getPastScores();
    past.push(score);
    localStorage.setItem('quiz_past_scores', JSON.stringify(past.slice(-10)));
  } catch {
    // ignore
  }
}
export function QuizResultsScreen({
  score,
  totalQuestions,
  roundScores,
  onPlayAgain
}: QuizResultsScreenProps) {
  const accuracy = Math.round(score / totalQuestions * 100);
  const tier = getPerformanceTier(accuracy);
  const TierIcon = tier.icon;
  const [pastScores, setPastScores] = useState<number[]>([]);
  useEffect(() => {
    const prev = getPastScores();
    setPastScores(prev);
    savePastScore(score);
  }, [score]);
  const bestScore = pastScores.length > 0 ? Math.max(...pastScores) : null;
  const isNewBest = bestScore === null || score > bestScore;
  // Build leaderboard with user inserted
  const leaderboard: LeaderboardEntry[] = [...SIMULATED_PLAYERS.map(p => ({
    ...p,
    isYou: false
  })), {
    name: 'You',
    score,
    accuracy,
    isYou: true
  }].sort((a, b) => b.score - a.score || b.accuracy - a.accuracy).slice(0, 10);
  const yourRank = leaderboard.findIndex(e => e.isYou) + 1;
  const specialties: Specialty[] = ['Nephrologist', 'Cardiologist', 'Endocrinologist', 'PCP'];
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 md:px-6 overflow-y-auto" data-id="element-215">
      <div className="max-w-5xl mx-auto" data-id="element-216">
        {/* Hero */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        type: 'spring'
      }} className="text-center mb-10" data-id="element-217">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{
          backgroundColor: `${tier.color}20`
        }} data-id="element-218">
            <TierIcon size={40} style={{
            color: tier.color
          }} data-id="element-219" />
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-2 text-dark" data-id="element-220">
            Quiz Complete!
          </h1>
          <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mt-2 mb-4" style={{
          backgroundColor: `${tier.color}15`,
          color: tier.color
        }} data-id="element-221">
            {tier.label}
          </div>
          <p className="text-lg text-gray-600 max-w-lg mx-auto" data-id="element-222">
            {tier.message}
          </p>
        </motion.div>

        {/* Score + Rank Cards */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-id="element-223">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center text-center" data-id="element-224">
            <Target className="text-brand mb-3" size={28} data-id="element-225" />
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-226">
              Score
            </p>
            <div className="text-4xl font-black text-dark" data-id="element-227">
              {score}
              <span className="text-xl text-gray-400" data-id="element-228">/{totalQuestions}</span>
            </div>
            <p className="text-sm font-medium text-gray-500 mt-1" data-id="element-229">
              {accuracy}% Accuracy
            </p>
            {isNewBest && pastScores.length > 0 && <span className="inline-block mt-2 px-3 py-1 rounded-full bg-amber-50 text-amber-600 text-xs font-bold uppercase tracking-wider" data-id="element-230">
                🎉 New Best!
              </span>}
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200 flex flex-col items-center justify-center text-center" data-id="element-231">
            <Medal className="text-brand mb-3" size={28} data-id="element-232" />
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-233">
              Your Rank
            </p>
            <div className="text-4xl font-black text-dark" data-id="element-234">#{yourRank}</div>
            <p className="text-sm font-medium text-gray-500 mt-1" data-id="element-235">
              out of {leaderboard.length} players
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-200" data-id="element-236">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4 text-center" data-id="element-237">
              Round Breakdown
            </p>
            <div className="space-y-3" data-id="element-238">
              {GAME_DATA.map((round, i) => <div key={round.id} className="flex items-center justify-between" data-id="element-239">
                  <span className="font-medium text-gray-700 text-sm" data-id="element-240">
                    {round.title}
                  </span>
                  <div className="flex items-center" data-id="element-241">
                    <div className="w-20 h-2 bg-gray-100 rounded-full mr-2 overflow-hidden" data-id="element-242">
                      <motion.div className="h-full rounded-full" style={{
                    backgroundColor: roundScores[i] === 4 ? '#22c55e' : roundScores[i] >= 3 ? '#3b82f6' : roundScores[i] >= 2 ? '#f97316' : '#ef4444'
                  }} initial={{
                    width: 0
                  }} animate={{
                    width: `${roundScores[i] / 4 * 100}%`
                  }} transition={{
                    duration: 1,
                    delay: 0.5 + i * 0.2
                  }} data-id="element-243" />
                    </div>
                    <span className="font-bold text-dark text-sm w-7 text-right" data-id="element-244">
                      {roundScores[i]}/4
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Leaderboard */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-200 mb-10" data-id="element-245">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-246">
            <Users size={24} className="text-brand" data-id="element-247" />
            <h2 className="text-2xl font-bold text-dark" data-id="element-248">Team Scorecard</h2>
          </div>

          <div className="space-y-2" data-id="element-249">
            {leaderboard.map((entry, i) => {
            const rankIcon = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
            return <motion.div key={entry.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.3,
              delay: 0.5 + i * 0.05
            }} className={`flex items-center justify-between p-4 rounded-2xl transition-all ${entry.isYou ? 'bg-brand/5 border-2 border-brand/30 shadow-sm' : 'bg-gray-50 border border-transparent hover:bg-gray-100'}`} data-id="element-250">
                  <div className="flex items-center gap-4" data-id="element-251">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black ${entry.isYou ? 'bg-brand text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-252">
                      {rankIcon || i + 1}
                    </div>
                    <div data-id="element-253">
                      <span className={`font-bold ${entry.isYou ? 'text-brand' : 'text-dark'}`} data-id="element-254">
                        {entry.name}
                      </span>
                      {entry.isYou && <span className="ml-2 text-xs font-bold uppercase tracking-wider text-brand bg-brand/10 px-2 py-0.5 rounded-full" data-id="element-255">
                          You
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-6" data-id="element-256">
                    <div className="text-right" data-id="element-257">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-258">
                        Score
                      </span>
                      <p className={`text-xl font-black ${entry.isYou ? 'text-brand' : 'text-dark'}`} data-id="element-259">
                        {entry.score}/{totalQuestions}
                      </p>
                    </div>
                    <div className="text-right hidden md:block" data-id="element-260">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-261">
                        Accuracy
                      </span>
                      <p className={`text-xl font-black ${entry.isYou ? 'text-brand' : 'text-dark'}`} data-id="element-262">
                        {entry.accuracy}%
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* Personal History */}
        {pastScores.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-200 mb-10" data-id="element-263">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-264">
              <TrendingUp size={24} className="text-brand" data-id="element-265" />
              <h2 className="text-2xl font-bold text-dark" data-id="element-266">Your History</h2>
            </div>
            <div className="flex items-end justify-center gap-2 h-32" data-id="element-267">
              {pastScores.map((s, i) => {
            const height = s / totalQuestions * 100;
            const barColor = s >= 10 ? '#22c55e' : s >= 7 ? '#3b82f6' : s >= 4 ? '#f97316' : '#ef4444';
            return <motion.div key={i} initial={{
              height: 0
            }} animate={{
              height: `${height}%`
            }} transition={{
              duration: 0.6,
              delay: 0.7 + i * 0.1
            }} className="w-10 rounded-t-lg relative group" style={{
              backgroundColor: barColor
            }} data-id="element-268">
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-600" data-id="element-269">
                      {s}
                    </div>
                  </motion.div>;
          })}
              {/* Current score */}
              <motion.div initial={{
            height: 0
          }} animate={{
            height: `${score / totalQuestions * 100}%`
          }} transition={{
            duration: 0.6,
            delay: 0.7 + pastScores.length * 0.1
          }} className="w-10 rounded-t-lg relative border-2 border-brand" style={{
            backgroundColor: '#E2001A'
          }} data-id="element-270">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-black text-brand" data-id="element-271">
                  {score}
                </div>
                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-brand uppercase" data-id="element-272">
                  Now
                </div>
              </motion.div>
            </div>
            <div className="text-center mt-8 text-sm text-gray-500" data-id="element-273">
              {pastScores.length} previous{' '}
              {pastScores.length === 1 ? 'attempt' : 'attempts'} • Best:{' '}
              {Math.max(...pastScores)}/{totalQuestions}
            </div>
          </motion.div>}

        {/* Specialty Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="mb-12" data-id="element-274">
          <h2 className="text-2xl font-bold mb-6 text-center text-dark" data-id="element-275">
            Specialty Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-276">
            {specialties.map((spec, i) => <motion.div key={spec} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.9 + i * 0.1
          }} className="bg-white rounded-2xl p-6 shadow-sm border-t-4" style={{
            borderColor: SPECIALTY_COLORS[spec]
          }} data-id="element-277">
                <h3 className="text-xl font-bold mb-4" style={{
              color: SPECIALTY_COLORS[spec]
            }} data-id="element-278">
                  {spec}s
                </h3>
                <ul className="space-y-3" data-id="element-279">
                  {GAME_DATA.map(round => <li key={round.id} className="flex items-start text-sm" data-id="element-280">
                      <CheckCircle2 size={16} className="mr-2 mt-0.5 flex-shrink-0" style={{
                  color: SPECIALTY_COLORS[spec]
                }} data-id="element-281" />
                      <span className="text-gray-700" data-id="element-282">
                        <strong className="text-gray-900" data-id="element-283">
                          {round.title}:
                        </strong>{' '}
                        {round.answers[spec]}
                      </span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 1.2
      }} className="text-center pb-12" data-id="element-284">
          <button onClick={onPlayAgain} className="inline-flex items-center justify-center px-10 py-4 text-lg font-bold text-white bg-dark rounded-full hover:bg-black transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105" data-id="element-285">
            <RotateCcw className="mr-2" size={20} data-id="element-286" />
            Play Again
          </button>
        </motion.div>
      </div>
    </div>;
}